import { DPRFormData, TargetEntry } from "src/types/dpr.types";
import {
  SubmitDprPayload,
  InterventionPayload,
  DeclarationsPayload,
  DprYearTargets,
  DprTargetEntry,
  TreeSpeciesItem,
} from "src/utils/api/dpr";

/**
 * Year number to year label mapping for backend DTO
 */
const YEAR_LABELS: Record<number, string> = {
  1: "1st Year",
  2: "2nd Year",
  3: "3rd Year",
  4: "4th Year",
  5: "5th Year",
  6: "6th Year",
  7: "7th Year",
  8: "8th Year",
  9: "9th Year",
  10: "10th Year",
};

/**
 * Transforms frontend DPRFormData into the backend SubmitDprPayload.
 *
 * Maps field names from the React form structure to the NestJS DTO structure:
 *   - Part B (Site Characteristics) → intervention (dpr_intervention columns — land_specification stays untouched)
 *   - Part C (Interventions & Zoning) → intervention (dpr_intervention columns)
 *   - Part C (Year-wise Targets) → yearwiseTargets (targets table rows)
 *   - Part D (Declarations) → declarations
 */
export const transformDprFormToPayload = (
  formData: DPRFormData,
  projectDetailId: string,
  landOwnershipEpid: string,
  status?: string
): SubmitDprPayload => {
  return {
    projectDetailId,
    landOwnershipEpid,
    status,
    intervention: buildIntervention(formData, projectDetailId),
    yearwiseTargets: buildYearwiseTargets(formData),
    declarations: buildDeclarations(formData),
  };
};

/**
 * Safely parse a value to a number, returning undefined if not a valid number
 */
const toNum = (val: any): number | undefined => {
  if (val === undefined || val === null || val === "") return undefined;
  const n = typeof val === "number" ? val : parseFloat(val);
  return isNaN(n) ? undefined : n;
};

/**
 * Part C — Maps frontend form fields → backend dpr_intervention column names
 * Now also includes Part B (Site Characteristics) so everything goes to dpr_intervention
 */
const buildIntervention = (
  formData: DPRFormData,
  projectDetailId: string
): InterventionPayload => ({
  projectDetailId,
  // Zoning
  interventionType: formData.interventionType,
  kmlFileId: formData.kmlFileId,
  kmlFileName: formData.kmlFileName,
  // Tree Plantation
  treePlantationArea: toNum(formData.treePlantationArea),
  treePlantationSpecies: formData.treePlantationSpecies,
  seedlingsPerHectare: toNum(formData.seedlingsPerHectare),
  spacingPattern: formData.spacingPattern,
  nurserySource: formData.nurserySource,
  nurseryLocation: formData.nurseryLocation,
  temporaryNurseryDetails: formData.temporaryNurseryDetails,
  maintenancePlan: formData.maintenancePlan,
  treeSpecies: formData.treeSpecies?.map(
    (ts): TreeSpeciesItem => ({
      botanicalName: ts.botanicalName,
      commonName: ts.commonName,
      seedlings: ts.seedlings,
      spacing: ts.spacing,
      nurseryDetails: ts.nurseryDetails,
    })
  ),
  // Soil & Moisture Conservation
  soilConservationArea: toNum(formData.soilConservationArea),
  conservationWorkType: formData.conservationWorkType,
  conservationWorkZone: formData.conservationWorkZone,
  conservationWorkPurpose: formData.conservationWorkPurpose,
  // Water Supply
  waterSupplyArea: toNum(formData.waterSupplyArea),
  proposedWaterSource: formData.proposedWaterSource,
  supplyLineLayout: formData.supplyLineLayout,
  waterHarvestingStructures: formData.waterHarvestingStructures,
  // Protection
  protectionArea: toNum(formData.protectionArea),
  fencingType: formData.fencingType,
  fencingLength: toNum(formData.fencingLength),
  firePreventionWorks: formData.firePreventionWorks,
  watchWardArrangements: formData.watchWardArrangements,
  // Biodiversity
  biodiversityArea: toNum(formData.biodiversityArea),
  pollinatorSpecies: formData.pollinatorSpecies,
  ntfpSpecies: formData.ntfpSpecies,
  grassesShrubsMedicinal: formData.grassesShrubsMedicinal,
  // DPR Purpose & GC Utilisation
  dprPurpose: formData.dprPurpose,
  estimatedGreenCredits: toNum(formData.estimatedGreenCredits),
  gcUtilisationDescription: formData.greenCreditUsageProjects,
  // Site Characteristics (Part B) — stored in dpr_intervention to keep land_specification untouched
  landArea: toNum(formData.landArea),
  landUseCategory: formData.landUseCategory,
  acknowledgementDate: formData.acknowledgementDate,
  legalStatusOfLand: formData.landLegalStatus,
  terrain: formData.topography,
  slope: formData.slopePercentage?.toString(),
  climaticZone: formData.climaticZone,
  soilType: formData.soilType,
  soilDepthAndTexture: formData.soilDepthTexture,
  soilMoistureRegime: formData.soilMoistureRegime,
  soilpH: formData.soilPH?.toString(),
  sourceOfWater: formData.waterSource,
  siteWaterSupplyLines: formData.waterSupplyLines,
  distanceToNearestWaterSource:
    formData.distanceToNearestWaterSource?.toString(),
  typeOfVegetation: formData.vegetationType,
  dominantSpecies: formData.dominantSpecies,
  treeCanopyDensity: formData.canopyDensity,
  presenceOfInvasiveSpecies: formData.invasiveSpeciesPresent,
  invasiveSpeciesDetails: formData.invasiveSpeciesDetails,
  existingFauna: formData.existingFauna,
  proximityToLocalCommunities: formData.proximityToLocalCommunities?.toString(),
  rightsAndUsage: formData.rightsAndUsage,
  communityDependency: formData.communityDependency,
  anyExistingRestrictions: formData.existingRestrictions,
  accessibility: formData.distanceFromRoad?.toString(),
  circleName: formData.circle,
  nearestRoadType: formData.nearestRoadType,
  approachRoute: formData.approachRoute,
  inspectionPaths: formData.inspectionPaths,
  soilFertilityStatus: formData.soilFertilityStatus,
  suitabilityForConservation: formData.suitabilityForConservation,
  treesGirth0To10: formData.treesGirth0to10,
  treesGirth10To30: formData.treesGirth10to30,
  treesGirth30To60: formData.treesGirth30to60,
  treesGirthGreater60: formData.treesGirthGreater60,
  // GCA / SNO / DNO User IDs
  gcaUserId: formData.gcaId,
  snoUserId: formData.stateNodalOfficerId,
  dnoUserId: formData.districtNodalOfficerId,
  // Project / Location Info
  projectName: formData.projectName,
  projectType: formData.projectType,
  landParcelId: formData.landParcelId,
  landParcelName: formData.landParcelName,
  state: formData.state,
  district: formData.district,
  block: formData.block,
  village: formData.village,
  division: formData.division,
  rangeName: formData.range,
  nearbySettlements: formData.nearbySttlements,
  distanceFromHabitation: formData.distanceFromHabitation?.toString(),
  areaUnit: formData.areaUnit,
  // Declarations
  declarationTrueComplete: formData.declarationTrueComplete,
  declarationAbideRules: formData.declarationAbideRules,
  declarationAdequateResources: formData.declarationAdequateResources,
  declarationMaintenanceFund: formData.declarationMaintenanceFund,
  declarationPerformanceParameters: formData.declarationPerformanceParameters,
  declarationProgressReports: formData.declarationProgressReports,
  declarationNoLandClaim: formData.declarationNoLandClaim,
  declarationNoCommercialActivity: formData.declarationNoCommercialActivity,
});

/**
 * Collects year-wise targets from formData.yearwiseTargetsData1..10
 * and transforms them into the backend DprYearTargets[] format.
 * Only includes years that have at least one target entry.
 */
const buildYearwiseTargets = (formData: DPRFormData): DprYearTargets[] => {
  const yearwiseTargets: DprYearTargets[] = [];

  for (let i = 1; i <= 10; i++) {
    const key = `yearwiseTargetsData${i}` as keyof DPRFormData;
    const targets = formData[key] as TargetEntry[] | undefined;

    if (targets && targets.length > 0) {
      const validTargets = targets
        .filter((t) => t.indicator && t.activity) // skip empty rows
        .map(
          (t): DprTargetEntry => ({
            indicator: t.indicator,
            activity: t.activity,
            unit: t.unit,
            numberOfUnits: t.numberOfUnits,
            costPerUnit: t.costPerUnit,
            costRequired: t.costRequired,
            description: t.description,
          })
        );

      if (validTargets.length > 0) {
        yearwiseTargets.push({
          year: YEAR_LABELS[i],
          targets: validTargets,
        });
      }
    }
  }

  return yearwiseTargets;
};

/**
 * Part D — Maps frontend declaration checkboxes to backend DTO
 */
const buildDeclarations = (formData: DPRFormData): DeclarationsPayload => ({
  declarationTrueComplete: formData.declarationTrueComplete,
  declarationAbideRules: formData.declarationAbideRules,
  declarationAdequateResources: formData.declarationAdequateResources,
  declarationMaintenanceFund: formData.declarationMaintenanceFund,
  declarationPerformanceParameters: formData.declarationPerformanceParameters,
  declarationProgressReports: formData.declarationProgressReports,
  declarationNoLandClaim: formData.declarationNoLandClaim,
  declarationNoCommercialActivity: formData.declarationNoCommercialActivity,
});
